/**
 * Language Context for managing application language
 */

import React, {
    createContext,
    useContext,
    useState,
    useEffect,
} from 'react'
import {
    useTranslation,
} from 'react-i18next'
import type {
    LanguageCode,
} from '../../../shared/lang'

interface LanguageContextType {
    language: LanguageCode
    changeLanguage: (lang: LanguageCode) => void
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const LanguageProvider: React.FC<{
    children: React.ReactNode,
}> = ({
    children,
}) => {
    const {
        i18n,
    } = useTranslation()
    const [language, setLanguage] = useState<LanguageCode>(
        (localStorage.getItem('language') as LanguageCode) || 'cs'
    )

    useEffect(() => {
        i18n.changeLanguage(language)
    }, [language, i18n])

    const changeLanguage = (lang: LanguageCode) => {
        setLanguage(lang)
        localStorage.setItem('language', lang)
        i18n.changeLanguage(lang)
    }

    return (
        <LanguageContext.Provider value={{
            language,
            changeLanguage,
        }}>
            {children}
        </LanguageContext.Provider>
    )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = () => {
    const context = useContext(LanguageContext)
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider')
    }
    return context
}
